import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { CustomerCommEntry } from "@/data/types";

function isMissingCustomerCommsTable(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const code = "code" in error ? error.code : undefined;
  const message = "message" in error ? error.message : undefined;
  return code === "PGRST205" || (typeof message === "string" && message.includes("customer_comms"));
}

function mapDbCustomerCommToCustomerComm(dbComm: Record<string, unknown>): CustomerCommEntry {
  return {
    id: dbComm.id as string,
    date: dbComm.date as string,
    time: dbComm.time as string,
    commType: dbComm.comm_type as CustomerCommEntry['commType'],
    summary: dbComm.summary as string,
    relatedJobId: dbComm.related_job_id as string | undefined,
    user: dbComm.user as string,
    source: dbComm.source as CustomerCommEntry['source'],
    externalRef: dbComm.external_ref as CustomerCommEntry['externalRef'],
    origin: dbComm.origin as CustomerCommEntry['origin'],
  };
}

export const CustomerCommService = {
  async fetchCustomerComms(): Promise<CustomerCommEntry[]> {
    try {
      const { data, error } = await supabase
        .from("customer_comms")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        if (isMissingCustomerCommsTable(error)) return [];
        console.warn("Error fetching customer comms from Supabase:", error.message);
        return [];
      }

      if (!data) return [];
      return data.map(mapDbCustomerCommToCustomerComm);
    } catch (err) {
      console.warn("Exception fetching customer comms:", err);
      return [];
    }
  },

  async fetchCustomerCommById(id: string): Promise<CustomerCommEntry | null> {
    try {
      const { data, error } = await supabase
        .from("customer_comms")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        if (isMissingCustomerCommsTable(error)) return null;
        console.warn(`Error fetching customer comm ${id}:`, error.message);
        return null;
      }

      return data ? mapDbCustomerCommToCustomerComm(data) : null;
    } catch (err) {
      console.warn(`Exception fetching customer comm ${id}:`, err);
      return null;
    }
  },

  async fetchCustomerCommsByJobId(jobId: string): Promise<CustomerCommEntry[]> {
    try {
      const { data, error } = await supabase
        .from("customer_comms")
        .select("*")
        .eq("related_job_id", jobId)
        .order("date", { ascending: false });

      if (error) {
        if (isMissingCustomerCommsTable(error)) return [];
        console.warn(`Error fetching customer comms for job ${jobId}:`, error.message);
        return [];
      }

      if (!data) return [];
      return data.map(mapDbCustomerCommToCustomerComm);
    } catch (err) {
      console.warn(`Exception fetching customer comms for job ${jobId}:`, err);
      return [];
    }
  },

  async createCustomerComm(comm: Partial<CustomerCommEntry>): Promise<CustomerCommEntry | null> {
    try {
      let organizationId: string | null = null;
      try { organizationId = await getCurrentOrganizationId(); } catch { /* proceed without */ }

      const dbComm: Record<string, unknown> = {
        id: comm.id,
        date: comm.date,
        time: comm.time,
        comm_type: comm.commType,
        summary: comm.summary,
        related_job_id: comm.relatedJobId,
        user: comm.user,
        source: comm.source,
        external_ref: comm.externalRef,
        origin: comm.origin,
        organization_id: organizationId,
      };

      const { data, error } = await supabase
        .from("customer_comms")
        .insert(dbComm)
        .select()
        .single();

      if (error) {
        if (isMissingCustomerCommsTable(error)) return null;
        console.warn("Error creating customer comm:", error.message);
        return null;
      }

      return data ? mapDbCustomerCommToCustomerComm(data) : null;
    } catch (err) {
      console.warn("Exception creating customer comm:", err);
      return null;
    }
  },

  async updateCustomerComm(id: string, updates: Partial<CustomerCommEntry>): Promise<CustomerCommEntry | null> {
    try {
      const dbComm: Record<string, unknown> = {};

      if (updates.date !== undefined) dbComm.date = updates.date;
      if (updates.time !== undefined) dbComm.time = updates.time;
      if (updates.commType !== undefined) dbComm.comm_type = updates.commType;
      if (updates.summary !== undefined) dbComm.summary = updates.summary;
      if (updates.relatedJobId !== undefined) dbComm.related_job_id = updates.relatedJobId;
      if (updates.user !== undefined) dbComm.user = updates.user;
      if (updates.source !== undefined) dbComm.source = updates.source;
      if (updates.externalRef !== undefined) dbComm.external_ref = updates.externalRef;
      if (updates.origin !== undefined) dbComm.origin = updates.origin;

      const { data, error } = await supabase
        .from("customer_comms")
        .update(dbComm)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        if (isMissingCustomerCommsTable(error)) return null;
        console.warn(`Error updating customer comm ${id}:`, error.message);
        return null;
      }

      return data ? mapDbCustomerCommToCustomerComm(data) : null;
    } catch (err) {
      console.warn(`Exception updating customer comm ${id}:`, err);
      return null;
    }
  },

  subscribeToCustomerComms(callback: (comms: CustomerCommEntry[]) => void): () => void {
    try {
      let disposed = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      let inFlight = false;

      const refresh = async () => {
        if (disposed || inFlight) return;
        inFlight = true;
        try {
          const comms = await CustomerCommService.fetchCustomerComms();
          if (!disposed) callback(comms);
        } finally {
          inFlight = false;
        }
      };

      const scheduleRefresh = () => {
        if (disposed) return;
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          void refresh();
        }, 250);
      };

      // If the table is not present in the connected Supabase project yet,
      // treat customer comms as an unavailable optional feature.
      const channel = supabase
        .channel('customer-comms-changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'customer_comms' },
          scheduleRefresh
        )
        .subscribe();

      return () => {
        disposed = true;
        if (timer) clearTimeout(timer);
        supabase.removeChannel(channel);
      };
    } catch (err) {
      console.warn("Exception subscribing to customer comms:", err);
      return () => {};
    }
  },
};
